import { useRef, useMemo } from 'react';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';

import { GridRowModes, GridToolbarContainer, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';

import { dataGridPremiumColumns, getJokerFieldType } from '../../utils/helper';
import {
  DataGridIdentifiers,
  FormErrorUI,
  AddButtonUi,
  AutoCompleteSelectUI,
  DatagridPremiumUi,
  InputUI,
  CheckboxUI,
} from '../Interface';
import { IssueMethodList } from '../../config/production';
import useGenerateJokerCols from '../../hooks/useGenerateJokerCols';

import useConfig from '../../context/ConfigContext';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
const EditToolbar = (props) => {
  const { disabled, push, defaultItem } = props;
  const { values, validateField } = useFormikContext();

  const handleClick = async () => {
    defaultItem.id = uuidv4();
    defaultItem.isNew = true;
    defaultItem.quantityUnitId = null;
    await push(defaultItem);

    await validateField(`sourceListItem.${values.sourceListItem.length ?? 0}.itemCode`);
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
};

const SourceListItem = ({ disabled, defaultItem, autocompleteOptions, autocompleteData, jokerLineFields }) => {
  const { config } = useConfig();
  const focusedFieldRef = useRef(null);
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  const getValueIndex = (values, id) => {
    return values.sourceListItem.findIndex((rc) => rc.id === id);
  };
  const jokerColumns = useGenerateJokerCols(jokerLineFields, 'sourceListItem', getValueIndex);

  const columns = useMemo(
    () => (remove) =>
      dataGridPremiumColumns([
        {
          field: 'itemCode',
          headerName: 'Cikk',
          type: 'string',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions.items.find(
              (u) => u.value === values.sourceListItem?.[valueIndex]?.itemCode
            );
            return findedItem?.label;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions.items.find(
              (u) => u.value === values.sourceListItem?.[valueIndex]?.itemCode
            );
            return (
              <Field
                name={`sourceListItem.${valueIndex}.itemCode`}
                type="text"
                variant="standard"
                as={AutoCompleteSelectUI}
                fullWidth
                helperText={<FormErrorUI message={errors?.sourceListItem?.[valueIndex]?.itemCode} />}
                onChange={(_e, newVal) => {
                  const val = newVal?.value ?? null;
                  const findedI = autocompleteData.items.find((i) => i.itemCode === val);

                  if (
                    config.isManufacturedItemInsertable === false &&
                    findedI?.stockItemType === 'MANUFACTUREDPRODUCT'
                  ) {
                    addNotification({
                      content: 'A forrásjegyzékbe gyártott cikk nem rögzíthető alapanyagként!',
                      type: NotificationType.ERROR,
                    });
                    return;
                  }

                  setFieldValue(`sourceListItem.${valueIndex}.itemCode`, val);
                  setFieldValue(`sourceListItem.${valueIndex}.quantityUnitId`, findedI?.quantityUnitId ?? null);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: val,
                  });
                }}
                onBlur={async () => await validateField(`sourceListItem.${valueIndex}.itemCode`)}
                value={findedItem}
                isOptionEqualToValue={(option, value) => option.value === value}
                dataset={autocompleteOptions.items}
              />
            );
          },
        },

        {
          field: 'neededQuantity',
          headerName: 'Szükséges mennyiség',
          type: 'string',
          editable: true,
          sortable: false,
          valueGetter: (_data, row) => {
            const valueIndex = getValueIndex(values, row.id);
            return values?.sourceListItem?.[valueIndex]?.neededQuantity;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            return (
              <Field
                name={`sourceListItem.${valueIndex}.neededQuantity`}
                type="number"
                as={InputUI}
                variant="standard"
                fullWidth
                autoFocus={focusedFieldRef.current === 'neededQuantity'}
                onFocus={(e) => e.target.select()}
                onClick={(e) => e.target.select()}
                helperText={<FormErrorUI message={errors?.sourceListItem?.[valueIndex]?.neededQuantity} />}
                onChange={(e) => {
                  setFieldValue(`sourceListItem.${valueIndex}.neededQuantity`, e.target?.value);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target?.value,
                  });
                }}
                onBlur={async () => await validateField(`sourceListItem.${valueIndex}.neededQuantity`)}
              />
            );
          },
        },

        {
          field: 'quantityUnitId',
          headerName: 'Me.',
          type: 'string',
          filterable: false,
          sortable: false,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            const findedUnit = autocompleteData.quantityUnit.find(
              (u) => u.id === values.sourceListItem?.[valueIndex]?.quantityUnitId
            );
            return findedUnit?.code ?? '';
          },
        },

        {
          field: 'actions',
          type: 'actions',
          headerName: 'Szerk.',
          cellClassName: 'actions',
          sortable: false,
          getActions: ({ id, row }) => {
            const findedI = autocompleteData.items.find((i) => i.itemCode === row.itemCode);
            return [
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  const valueIndex = getValueIndex(values, id);
                  return remove(valueIndex);
                }}
                color="inherit"
                disabled={
                  disabled ||
                  (config.isManufacturedItemInsertable === false && findedI?.stockItemType === 'MANUFACTUREDPRODUCT')
                }
              />,
            ];
          },
        },

        {
          field: 'secondaryProduct',
          headerName: 'Melléktermék',
          type: 'boolean',
          editable: true,
          sortable: false,
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return (
              <Field
                name={`sourceListItem.${valueIndex}.secondaryProduct`}
                type="checkbox"
                component={CheckboxUI}
                helperText={<FormErrorUI message={errors?.sourceListItem?.[valueIndex]?.secondaryProduct} />}
                checked={values.sourceListItem?.[valueIndex]?.secondaryProduct ?? false}
                variant="standard"
                onChange={(e) => {
                  setFieldValue(`sourceListItem.${valueIndex}.secondaryProduct`, e.target.checked);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.checked,
                  });
                }}
              />
            );
          },
        },

        {
          field: 'whsCode',
          headerName: 'Raktár',
          type: 'string',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.warehouse.find((u) => {
              const sourcelisItemCode = values.sourceListItem?.[valueIndex]?.whsCode;
              return u.value === (sourcelisItemCode || values.whsCode);
            });

            return findedItem?.label;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.warehouse.find((u) => {
              const sourcelisItemCode = values.sourceListItem?.[valueIndex]?.whsCode;
              return u.value === (sourcelisItemCode || values.whsCode);
            });
            return (
              <Field
                name={`sourceListItem.${valueIndex}.whsCode`}
                as={AutoCompleteSelectUI}
                helperText={<FormErrorUI message={errors?.sourceListItem?.[valueIndex]?.whsCode} />}
                selectedValue={values.sourceListItem?.[valueIndex]?.whsCode}
                value={findedItem}
                onChange={(_e, newVal) => {
                  setFieldValue(`sourceListItem.${valueIndex}.whsCode`, newVal?.value ?? null);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: newVal?.value ?? null,
                  });
                }}
                dataset={autocompleteOptions?.warehouse}
                onBlur={async () => await validateField(`sourceListItem.${valueIndex}.whsCode`)}
              />
            );
          },
        },
        {
          field: 'issueMethod',
          headerName: 'Kiadás módja',
          type: 'string',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = IssueMethodList.find(
              (u) => u.value === values.sourceListItem?.[valueIndex]?.issueMethod
            );
            return findedItem?.label;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = IssueMethodList.find(
              (u) => u.value === values.sourceListItem?.[valueIndex]?.issueMethod
            );
            return (
              <Field
                name={`sourceListItem.${valueIndex}.issueMethod`}
                as={AutoCompleteSelectUI}
                helperText={<FormErrorUI message={errors?.sourceListItem?.[valueIndex]?.issueMethod} />}
                selectedValue={values.sourceListItem?.[valueIndex]?.issueMethod}
                value={findedItem}
                onChange={(_e, newVal) => {
                  setFieldValue(`sourceListItem.${valueIndex}.issueMethod`, newVal?.value ?? null);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: newVal?.value ?? null,
                  });
                }}
                dataset={IssueMethodList}
                onBlur={async () => await validateField(`sourceListItem.${valueIndex}.issueMethod`)}
              />
            );
          },
        },

        ...jokerColumns,
      ]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      jokerColumns,
      autocompleteOptions.items,
      autocompleteData.items,
      autocompleteData.quantityUnit,
      values.sourceListItem,
      errors,
      setFieldValue,
      validateField,
      disabled,
    ]
  );

  return (
    <>
      <div className="overflow-x-auto">
        <div className="align-middle">
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
            <FieldArray name="sourceListItem">
              {({ remove, push }) => (
                <DatagridPremiumUi
                  apiRef={apiRef}
                  datagridIdentifier={DataGridIdentifiers.sourceListItem}
                  height={480}
                  columns={columns(remove)}
                  rows={values?.sourceListItem ?? []}
                  editMode="row"
                  slots={{
                    toolbar: EditToolbar,
                  }}
                  slotProps={{
                    toolbar: {
                      disabled,
                      push,
                      defaultItem,
                    },
                  }}
                  onCellDoubleClick={(_params, event) => {
                    event.defaultMuiPrevented = true;
                  }}
                  headerFilters={false}
                  getRowHeight={() => 'auto'}
                  onCellClick={(params, event) => {
                    const findedI = autocompleteData.items.find((i) => i.itemCode === params.row.itemCode);
                    if (
                      disabled ||
                      (config.isManufacturedItemInsertable === false &&
                        findedI?.stockItemType === 'MANUFACTUREDPRODUCT')
                    )
                      return;
                    focusedFieldRef.current = params.field;
                    const rowsInEditMode = Object.keys(apiRef.current.state.editRows);

                    for (const rowId of rowsInEditMode) {
                      if (params.row.id.toString() !== rowId) {
                        apiRef.current.stopRowEditMode({ id: rowId });
                      }
                    }

                    const rowMode = apiRef.current.getRowMode(params.row.id);
                    if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                  }}
                  onRowEditStop={() => {
                    focusedFieldRef.current = null;
                  }}
                />
              )}
            </FieldArray>
          </div>
        </div>
      </div>
    </>
  );
};

export default SourceListItem;
